import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, Scale, DollarSign, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const Money = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    <div className="mt-1 relative">
      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-cobalt" />
    </div>
  </label>
);

// Admin fee-configuration + approval dialog for a pending/active attorney.
export default function AdminAttorneyApproveModal({ attorney, onClose, onDone }) {
  const [titles, setTitles] = useState([]);
  const [level, setLevel] = useState(attorney.membership_level || "");
  const [monthly, setMonthly] = useState(attorney.monthly_price ?? 499);
  const [claim, setClaim] = useState(attorney.claim_fee ?? 100);
  const [drone, setDrone] = useState(attorney.drone_fee ?? 250);
  const [busy, setBusy] = useState(false);
  const editing = attorney.attorney_status === "active";

  useEffect(() => {
    api.get("/admin/membership-titles").then(({ data }) => setTitles(data)).catch(() => {});
    if (attorney.monthly_price == null) {
      api.get("/attorney/membership").catch(() => {}); // noop; defaults already 499/100/250
    }
  }, [attorney]);

  const submit = async () => {
    if (!level.trim()) { toast.error("Enter a membership level title."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/attorneys/${attorney.user_id}/approve`, {
        membership_level: level.trim(),
        monthly_price: Number(monthly), claim_fee: Number(claim), drone_fee: Number(drone),
      });
      toast.success(editing ? "Fee configuration saved" : "Attorney approved — membership payment prompted");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="attorney-approve-modal">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-cobalt-light flex items-center justify-center"><Scale className="w-5 h-5 text-cobalt" /></div>
            <div>
              <h3 className="font-heading font-bold text-navy leading-tight">{editing ? "Edit fees & membership" : "Approve attorney"}</h3>
              <p className="text-xs text-slate-500">{attorney.name} · {attorney.firm_name || attorney.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 text-xs text-slate-500 grid grid-cols-2 gap-2">
            <div><span className="text-slate-400">Bar:</span> {attorney.bar_state || "—"} #{attorney.bar_number || "—"}</div>
            <div><span className="text-slate-400">Office:</span> {attorney.office_location || "—"}</div>
            <div className="flex items-center gap-1">{attorney.bar_verified ? <><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Bar verified</> : "Bar unverified"}</div>
            <div><span className="text-slate-400">Applied:</span> {attorney.attorney_applied_at ? new Date(attorney.attorney_applied_at).toLocaleDateString() : "—"}</div>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-slate-500">Membership level title</span>
            <input list="membership-title-suggestions" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="approve-membership-level"
              placeholder="e.g. Founding Firm Partner" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" />
            <datalist id="membership-title-suggestions">
              {titles.map((t) => <option key={t} value={t} />)}
            </datalist>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Money label="Monthly subscription" value={monthly} onChange={setMonthly} testid="approve-monthly-price" />
            <Money label="Marketplace claim fee" value={claim} onChange={setClaim} testid="approve-claim-fee" />
            <Money label="Drone deployment fee" value={drone} onChange={setDrone} testid="approve-drone-fee" />
          </div>

          <div className="rounded-2xl bg-cobalt-light/60 p-3 text-xs text-cobalt">
            On approval, the attorney is notified with their fee summary and prompted to accept terms and pay the monthly subscription. Portal access unlocks only after successful payment.
          </div>
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2.5 rounded-full text-slate-500 text-sm font-medium hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="approve-confirm-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {editing ? "Save configuration" : "Approve & prompt payment"}
          </button>
        </div>
      </div>
    </div>
  );
}
